import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  User as FirebaseUser,
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User, UserSegment, UserRole, Permission } from '../types';

// Define available permissions
export const PERMISSIONS: Record<string, Permission> = {
  VIEW_DASHBOARD: 'view_dashboard',
  MANAGE_TRANSACTIONS: 'manage_transactions',
  MANAGE_CUSTOMERS: 'manage_customers',
  MANAGE_PRODUCTS: 'manage_products',
  MANAGE_EXPENSES: 'manage_expenses',
  VIEW_REPORTS: 'view_reports',
  MANAGE_PAYROLL: 'manage_payroll',
  MANAGE_TAXES: 'manage_taxes',
  MANAGE_USERS: 'manage_users',
  ADMIN_ACCESS: 'admin_access'
};

// Define role-based permissions
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  admin: Object.values(PERMISSIONS) as Permission[],
  accountant: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.MANAGE_TRANSACTIONS,
    PERMISSIONS.MANAGE_CUSTOMERS,
    PERMISSIONS.MANAGE_PRODUCTS,
    PERMISSIONS.MANAGE_EXPENSES,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.MANAGE_TAXES
  ],
  user: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.MANAGE_TRANSACTIONS,
    PERMISSIONS.MANAGE_CUSTOMERS,
    PERMISSIONS.MANAGE_PRODUCTS,
    PERMISSIONS.MANAGE_EXPENSES,
    PERMISSIONS.VIEW_REPORTS
  ]
};

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userData: User | null;
  loading: boolean;
  signUp: (email: string, password: string, displayName: string, segment: UserSegment) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithFacebook: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (data: Partial<User>) => Promise<void>;
  updateUserSegment: (segment: UserSegment) => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Fetch additional user data from Firestore
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          setUserData(userDoc.data() as User);
        } else {
          // If user exists in auth but not in Firestore, create a basic record
          const newUser: User = {
            id: user.uid,
            email: user.email || '',
            displayName: user.displayName || 'User',
            photoURL: user.photoURL || undefined,
            role: 'user',
            segment: 'personal',
            createdAt: new Date(),
            updatedAt: new Date()
          };
          
          await setDoc(userDocRef, newUser);
          setUserData(newUser);
        }
      } else {
        setUserData(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signUp = async (email: string, password: string, displayName: string, segment: UserSegment) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update profile with display name
      await updateProfile(user, { displayName });
      
      // Create user document in Firestore
      const newUser: User = {
        id: user.uid,
        email: user.email || '',
        displayName: displayName,
        photoURL: user.photoURL || undefined,
        role: 'user',
        segment: segment,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await setDoc(doc(db, 'users', user.uid), newUser);
      setUserData(newUser);
    } catch (error) {
      console.error('Error signing up:', error);
      throw error;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Check if this is a new user
      const userDocRef = doc(db, 'users', result.user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        // Create user document in Firestore for new Google users
        const newUser: User = {
          id: result.user.uid,
          email: result.user.email || '',
          displayName: result.user.displayName || 'Google User',
          photoURL: result.user.photoURL || undefined,
          role: 'user',
          segment: 'personal', // Default segment
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        await setDoc(userDocRef, newUser);
        setUserData(newUser);
      }
    } catch (error) {
      console.error('Error logging in with Google:', error);
      throw error;
    }
  };

  const loginWithFacebook = async () => {
    try {
      const provider = new FacebookAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Check if this is a new user
      const userDocRef = doc(db, 'users', result.user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        // Create user document in Firestore for new Facebook users
        const newUser: User = {
          id: result.user.uid,
          email: result.user.email || '',
          displayName: result.user.displayName || 'Facebook User',
          photoURL: result.user.photoURL || undefined,
          role: 'user',
          segment: 'personal', // Default segment
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        await setDoc(userDocRef, newUser);
        setUserData(newUser);
      }
    } catch (error) {
      console.error('Error logging in with Facebook:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  };

  const updateUserProfile = async (data: Partial<User>) => {
    if (!currentUser || !userData) return;
    
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      
      // Update Firestore document
      const updatedData = {
        ...data,
        updatedAt: new Date()
      };
      
      await updateDoc(userDocRef, updatedData);
      
      // Update auth profile if display name or photo URL is changed
      if (data.displayName || data.photoURL) {
        await updateProfile(currentUser, {
          displayName: data.displayName || currentUser.displayName,
          photoURL: data.photoURL || currentUser.photoURL
        });
      }
      
      // Update local state
      setUserData({
        ...userData,
        ...data,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  const updateUserSegment = async (segment: UserSegment) => {
    if (!userData) return;
    
    try {
      await updateUserProfile({ segment });
    } catch (error) {
      console.error('Error updating segment:', error);
      throw error;
    }
  };

  const hasPermission = (permission: Permission): boolean => {
    if (!userData) return false;
    
    const userRole = userData.role;
    const userPermissions = ROLE_PERMISSIONS[userRole];
    
    return userPermissions.includes(permission);
  };

  const value = {
    currentUser,
    userData,
    loading,
    signUp,
    login,
    loginWithGoogle,
    loginWithFacebook,
    logout,
    resetPassword,
    updateUserProfile,
    updateUserSegment,
    hasPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 